import { useCallback, useEffect, useRef, useState } from 'react'
import type { AdminOverview } from '@shared/protocol'

/**
 * Painel do operador.
 *
 * A rota responde 404 tanto pra quem nao tem o cargo quanto pra quem so errou o
 * endereco — e de proposito, pra pagina nao confirmar que existe. Aqui isso
 * vira `negado`, e a tela mostra o mesmo "nada aqui" nos dois casos.
 */

/** De quanto em quanto tempo a lista se atualiza sozinha. */
const REFRESH_MS = 5_000

/**
 * Quantas falhas seguidas ate reclamar na tela.
 *
 * Num poll de 5 segundos, uma falha isolada e rotina: a rede engasga, o proxy
 * reinicia, o limite por IP recusa uma chamada. Pintar um erro por cima de uma
 * lista que esta correta so assusta quem esta olhando — e foi o que aconteceu.
 */
const FALHAS_ATE_RECLAMAR = 2

/**
 * `carregando` ate a primeira resposta; `negado` so no 404, que e definitivo e
 * encerra o poll. Falha de rede mantem `carregando` de proposito: ela nao diz
 * nada sobre permissao, entao vale seguir tentando.
 */
export type AdminStatus = 'carregando' | 'liberado' | 'negado'

export interface AdminState {
    status: AdminStatus
    overview: AdminOverview | null
    error: string | null
}

export function useAdminPanel() {
    const [state, setState] = useState<AdminState>({
        status: 'carregando',
        overview: null,
        error: null
    })
    /** Consulta ainda voltando — evita empilhar quando a rede engasga. */
    const emVoo = useRef(false)
    const falhasSeguidas = useRef(0)

    const registrarFalha = useCallback((mensagem: string) => {
        falhasSeguidas.current += 1
        if (falhasSeguidas.current < FALHAS_ATE_RECLAMAR) return
        setState((current) => ({ ...current, error: mensagem }))
    }, [])

    /**
     * `obrigatorio` e pra quem clicou em algo: o tick do relogio cede a vez a
     * uma consulta que ainda esta voltando, mas um clique nao pode ceder — a
     * tela ficaria mostrando o estado anterior a acao que a pessoa acabou de
     * mandar.
     */
    const refresh = useCallback(async (obrigatorio = false) => {
        if (emVoo.current && !obrigatorio) return
        emVoo.current = true
        try {
            const response = await fetch('/api/admin/overview')

            if (response.status === 404) {
                falhasSeguidas.current = 0
                setState({ status: 'negado', overview: null, error: null })
                return
            }
            if (!response.ok) {
                // O status vai junto: `429` (limite por IP) e `500` pedem
                // providencias bem diferentes, e sem ele a mensagem nao ajuda.
                registrarFalha(`Nao consegui carregar as salas (erro ${response.status}).`)
                return
            }

            const overview = await response.json() as AdminOverview
            falhasSeguidas.current = 0
            setState({ status: 'liberado', overview, error: null })
        } catch {
            registrarFalha('Nao consegui falar com o servidor.')
        } finally {
            emVoo.current = false
        }
    }, [registrarFalha])

    useEffect(() => {
        void refresh()
    }, [refresh])

    // Sala e coisa viva: gente entrando, saindo e comecando a transmitir. O
    // poll segue enquanto houver chance de dar certo — so o 404 o encerra, e
    // por isso a primeira tentativa que falha por rede nao deixa a tela parada
    // pra sempre.
    useEffect(() => {
        if (state.status === 'negado') return
        const timer = setInterval(() => void refresh(), REFRESH_MS)
        return () => clearInterval(timer)
    }, [state.status, refresh])

    const act = useCallback(async (code: string, action: string, body: unknown) => {
        try {
            const response = await fetch(`/api/admin/rooms/${encodeURIComponent(code)}/${action}`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(body)
            })
            if (!response.ok) registrarFalha(`A acao falhou (erro ${response.status}).`)
        } catch {
            registrarFalha('A acao nao chegou ao servidor.')
        }
        // Recarrega mesmo em caso de falha: e assim que a tela volta a mostrar o
        // que de fato aconteceu, em vez do que a gente esperava que acontecesse.
        await refresh(true)
    }, [refresh, registrarFalha])

    return {
        ...state,
        refresh: () => void refresh(true),
        kick: (code: string, id: string) => void act(code, 'kick', { id }),
        setAdmin: (code: string, id: string, admin: boolean) => void act(code, 'promote', { id, admin }),
        unban: (code: string, id: string) => void act(code, 'unban', { id }),
        deleteRoom: async (code: string) => {
            try {
                const response = await fetch(`/api/admin/rooms/${encodeURIComponent(code)}`, { method: 'DELETE' })
                if (!response.ok) registrarFalha(`Nao consegui apagar a sala (erro ${response.status}).`)
            } catch {
                registrarFalha('Nao consegui apagar a sala.')
            }
            await refresh(true)
        }
    }
}
